import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CornerDownRight, Trash2, MessagesSquare } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/utils";

export default function CommodityForum({ symbol, name }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["forum", symbol],
    queryFn: async () => (await api.get(`/commodities/${symbol}/forum`)).data.items,
    refetchInterval: 15000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["forum", symbol] });
  const items = data || [];

  return (
    <div data-testid="commodity-forum">
      <Composer symbol={symbol} placeholder={`Share your ${name} market insight…`} onDone={refresh} testid="forum-new" />
      {isLoading ? [0, 1].map((i) => <div key={i} className="skeleton mt-3 h-24 w-full" />) : items.length === 0 ? (
        <div className="goe-card mt-4 flex flex-col items-center px-6 py-10 text-center text-sm text-dim" data-testid="forum-empty">
          <MessagesSquare size={34} className="mb-3 opacity-50" />
          No discussions yet. Start the first {name} thread.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((t, i) => <Thread key={t.id} t={t} i={i} symbol={symbol} onChange={refresh} />)}
        </div>
      )}
    </div>
  );
}

function Thread({ t, i, symbol, onChange }) {
  const [replying, setReplying] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3) }}
                className="goe-card p-4" data-testid={`forum-thread-${t.id}`}>
      <Post p={t} onChange={onChange} />
      {t.replies.length > 0 && (
        <div className="mt-3 space-y-3 border-l-2 pl-3" style={{ borderColor: "rgba(245,158,11,0.3)" }}>
          {t.replies.map((r) => <Post key={r.id} p={r} small onChange={onChange} />)}
        </div>
      )}
      {replying ? (
        <div className="mt-3"><Composer symbol={symbol} parentId={t.id} placeholder="Write a reply…" small testid={`forum-reply-${t.id}`}
                                        onDone={() => { setReplying(false); onChange(); }} /></div>
      ) : (
        <button onClick={() => setReplying(true)} data-testid={`forum-reply-btn-${t.id}`}
                className="pressable mt-3 flex items-center gap-1 text-xs font-bold accent">
          <CornerDownRight size={13} /> Reply{t.replies.length ? ` · ${t.replies.length}` : ""}
        </button>
      )}
    </motion.div>
  );
}

function Post({ p, small, onChange }) {
  const { user } = useAuth();
  const del = async () => {
    try { await api.delete(`/forum/${p.id}`); onChange(); } catch { toast.error("Could not delete"); }
  };
  const size = small ? 28 : 36;
  return (
    <div className="flex gap-3" data-testid={`forum-post-${p.id}`}>
      <div className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent text-xs font-bold text-white"
           style={{ width: size, height: size }}>
        {p.author?.avatar ? <img src={fileUrl(p.author.avatar)} alt="" className="h-full w-full object-cover" /> : (p.author?.name?.[0] || "?").toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold" style={{ color: "var(--text)" }}>{p.author?.name}
            <span className="ml-2 text-xs font-medium text-dim">{(p.author?.roles || []).join(" · ")} · {timeAgo(p.created_at)}</span></p>
          {p.author?.id === user.id && (
            <button onClick={del} data-testid={`forum-delete-${p.id}`} className="pressable text-dim hover:text-red-400"><Trash2 size={13} /></button>
          )}
        </div>
        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed" style={{ color: "var(--text)" }}>{p.text}</p>
      </div>
    </div>
  );
}

function Composer({ symbol, parentId, placeholder, onDone, small, testid }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/commodities/${symbol}/forum`, { text: text.trim(), parent_id: parentId });
      setText(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="glass flex items-end gap-2 rounded-2xl p-2">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={small ? 1 : 2} maxLength={1000}
                placeholder={placeholder} data-testid={`${testid}-input`}
                className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
      <button onClick={send} disabled={busy || !text.trim()} data-testid={`${testid}-submit`}
              className="btn-accent flex h-10 w-10 shrink-0 items-center justify-center rounded-xl disabled:opacity-40">
        <Send size={16} />
      </button>
    </div>
  );
}
